// Render vlastního našeptávače (issue #97, epic #62).
//
// Protějšek `productCardRender.js`: ten staví kartu ve tvaru NATIVNÍ Shoptet
// karty, tenhle modul staví panel ve tvaru NATIVNÍHO našeptávače — tedy přesně
// ty `bs-wh-*` třídy, které `searchWhisperer.js` mapou `SKIN` navěšuje na uzly
// default JS (#74). Důsledek: jedna sada stylů (`_whisperer.scss`), dvě cesty
// k obsahu (nativní AJAX vs. `hledani.jipos.cz`), **nula nového layoutu**.
//
// Modul je ČISTÝ: bere model, vrací DOM. Nic nefetchuje, nezná stavy panelu.
// Kdo ho volá a kdy, řeší `searchWhisperer.js` — ten jediný vlastní kotvu
// `#header .search`.
//
// ODCHYLKY OD MOCKUPU (schváleno 2026-08-09; `screens/17-vyhledavani.md`
// §Desktop — našeptávač je nezná, vznikly ze srovnání s Luigi's Boxem v #97):
//   • **košíkové CTA v řádku** — kulaté tlačítko za cenou, viditelné až na
//     hover/fokus řádku (na mobilu vždy). Košík jede přes
//     `shoptet.cartShared.addToCart({productCode})`, protože index nemá
//     `price_id` ani číselné `productId` (search/README.md §DEGRADACE price_id).
//   • **chip „TOP"** u prvního produktu. Od 2026-08-09 stojí na reálném
//     prodejním signálu — noční sync z reportingu (#94) plní `revenue_90d`
//     a `/popular` hlásí `strategy: revenue_90d` (#92). U `/autocomplete`
//     chip pořád znamená „nejsilnější zásah dotazu", u zero-state
//     „nejprodávanější"; text „TOP" sedí na obojí.
//   • **počty u kategorií** (14, 9, …) — mockup je má, nativní whisperer je
//     nevydává, náš index ano (`categories[].count`).
//
// Řádek produktu je `li` se stretched linkem, ne `a`: uvnitř je tlačítko košíku
// a interaktivní prvek se do odkazu vnořit nesmí. Klávesnice si href bere
// z `data-bs-href` (viz `searchWhisperer.js`).

import { icon, iconHtml } from './icon.js';
import { saleEndState } from './saleEnd.js';
import { CAT_ICONS } from './menuModel.js';
import { stockAmountText } from './stockAmount.js';
import { currency } from './money.js';
import L from '../locale/cs/whispererRender.js';

const LABEL_SUGGESTS = L.suggests;
const LABEL_POPULAR_QUERIES = L.popularQueries;
const LABEL_PRODUCTS = L.products;
const LABEL_POPULAR_PRODUCTS = L.popularProducts;
const LABEL_HINT = L.hint;
const LABEL_CART = L.cart;
const LABEL_TOP = L.top;
const IN_STOCK_COLOR = '#009901';

// Popisky sekcí obsahu. Klíč = `kind` z modelu (viz `searchWhisperer.js`).
const GROUP_LABELS = L.groups;

function el(tag, cls, txt) {
  const node = document.createElement(tag);
  if (cls) node.className = cls;
  if (txt != null) node.textContent = txt;
  return node;
}

function eyebrow(text, cls) {
  return el('div', cls ? 'bs-wh-eyebrow ' + cls : 'bs-wh-eyebrow', text);
}

/** „Zobrazit všech 24 výsledků" — čeština má tři tvary, ne dva. */
export function resultsCta(total) {
  return L.resultsCta(total);
}

/** „24 výsledků" do nadpisu pravého sloupce. */
function resultsCount(total) {
  return L.resultsCount(total);
}

/** Ikona kategorie podle cesty odkazu (sdílený registr z menuModel). */
function catIcon(href) {
  const path = (href || '').replace(/^https?:\/\/[^/]+/, '');
  return CAT_ICONS[path] || 'grid';
}

// --------------------------------------------------------------- levý sloupec

// Našeptané dotazy: shodná část dotazu tučně, doplněk světle (mockup 17d).
// Bez shody se blok nevykreslí — degradace dle specky.
function buildSuggests(items, label, urlFor) {
  if (!items || !items.length) return null;

  const box = el('div', 'bs-wh-sug');
  box.appendChild(eyebrow(label));

  items.forEach(function (item) {
    const link = el('a', 'bs-wh-sug__item');
    link.href = urlFor(item.query);
    link.dataset.bsQuery = item.query;
    link.appendChild(icon('search'));
    link.appendChild(el('span', 'bs-wh-sug__hit', item.hit != null ? item.hit : item.query));
    if (item.rest) link.appendChild(el('span', 'bs-wh-sug__rest', item.rest));
    box.appendChild(link);
  });

  return box;
}

function buildGroup(group) {
  const items = (group.items || []).filter(function (item) {
    return item && item.name && item.url;
  });
  if (!items.length) return null;

  const frag = document.createDocumentFragment();
  frag.appendChild(eyebrow(GROUP_LABELS[group.kind] || group.label || ''));

  const list = el('ul', 'bs-wh-list bs-wh-list--' + group.kind);
  list.dataset.bsKind = group.kind;

  items.forEach(function (item) {
    const li = el('li');
    const link = el('a', 'bs-wh-link');
    link.href = item.url;

    if (group.kind === 'brand') {
      // značky jsou chipy — bez ikony i bez počtu (mockup: jen wordmark)
      link.textContent = item.name;
    } else {
      const box = el('span', 'bs-wh-ico');
      box.appendChild(icon(group.kind === 'cat' ? catIcon(item.url) : 'file'));
      link.appendChild(box);
      link.appendChild(el('span', 'bs-wh-label', item.name));
      // `count` nese jen kategorie; mockup ho ukazuje vpravo na řádku
      if (typeof item.count === 'number' && item.count > 0) {
        link.appendChild(el('span', 'bs-wh-link__count', String(item.count)));
      }
    }

    li.appendChild(link);
    list.appendChild(li);
  });

  frag.appendChild(list);
  return frag;
}

// -------------------------------------------------------------- pravý sloupec

// Poslední záchrana, když API cenu nenaformátuje (`price_formatted` normálně
// vždy přijde) — bez tisícového oddělovače, jen správný symbol/strana měny.
function rawPrice(n) {
  const c = currency();
  return c.left ? c.symbol + n : n + ' ' + c.symbol;
}

function priceText(card) {
  if (card.price_formatted) return card.price_formatted;
  return card.price === null || card.price === undefined ? '' : rawPrice(card.price);
}

function buildProduct(card, isTop) {
  const row = el('li', 'bs-wh-prod');
  row.dataset.bsHref = card.url || '';
  if (card.sku) row.dataset.bsCode = card.sku; // kotva analytiky (searchAnalytics.wireClicks)
  if (isTop) row.classList.add('bs-wh-prod--top');

  // stretched link přes celý řádek — kvůli tlačítku košíku uvnitř
  const link = el('a', 'bs-wh-prod__link');
  link.href = card.url || '';
  link.setAttribute('aria-label', card.name || '');
  link.setAttribute('tabindex', '-1'); // řádky se obsluhují šipkami, ne Tabem
  row.appendChild(link);

  const image = el('span', 'bs-wh-prod__image');
  if (card.image) {
    const img = el('img');
    img.src = card.image;
    img.alt = '';
    img.loading = 'lazy';
    img.width = 52;
    img.height = 52;
    image.appendChild(img);
  } else {
    image.appendChild(icon('photo'));
  }
  row.appendChild(image);

  const info = el('span', 'bs-wh-prod__info');
  // Chip a jméno musí být SOUROZENCI ve flex řádce, ne chip uvnitř jména:
  // `.bs-wh-prod__name` je na mobilu `-webkit-box` s clampem na 2 řádky a ten
  // dělá z každého potomka vlastní řádek — chip by ukousl půlku jména.
  const head = el('span', 'bs-wh-prod__head');
  if (isTop) head.appendChild(el('span', 'bs-wh-prod__chip', LABEL_TOP));
  head.appendChild(el('span', 'bs-wh-prod__name', card.name || ''));
  info.appendChild(head);

  const avail = card.availability || {};
  if (avail.label) {
    // POZOR: `amount === 0` znamená u indexu „neznámo", ne „došlo"
    // (search/README.md) → počet kusů se dopisuje jen při amount > 0, nad 5 ks
    // jako „>5 ks" (stockAmount.js — stejný práh jako nativní karty Shoptetu).
    const amountText = stockAmountText(avail.amount);
    const text = amountText ? avail.label + ' ' + amountText : avail.label;
    const line = el('span', 'bs-wh-prod__avail', text);
    if (avail.state === 'in_stock') line.style.color = IN_STOCK_COLOR;
    info.appendChild(line);
  }
  row.appendChild(info);

  const price = el('span', 'bs-wh-prod__price', priceText(card));
  const discounted = !!(card.price_before && card.price_before > card.price);
  if (discounted) {
    price.appendChild(el('s', null, card.price_before_formatted || rawPrice(card.price_before)));
  }
  // konec akční ceny pod cenou (bez tiku — panel žije vteřiny); jen u reálné slevy
  const sale = discounted && card.action_to ? saleEndState(card.action_to) : null;
  if (sale) price.appendChild(el('span', 'bs-wh-prod__sale', sale.short));
  row.appendChild(price);

  // Košík: nativní `form.pr-action` postavit nejde (index nemá `price_id` ani
  // číselné `productId`) → tlačítko + `shoptet.cartShared.addToCart`.
  // Klik obsluhuje `searchWhisperer.js` delegací, ať se listener neváže na řádek.
  if (card.sku && card.in_stock) {
    const cart = el('button', 'bs-wh-prod__cart');
    cart.type = 'button';
    cart.dataset.bsCart = card.sku;
    cart.setAttribute('aria-label', LABEL_CART + ': ' + (card.name || ''));
    cart.setAttribute('title', LABEL_CART);
    cart.appendChild(icon('cart'));
    row.appendChild(cart);
  }

  return row;
}

function buildProducts(model) {
  const products = model.products || [];
  if (!products.length) return null;

  const main = el('div', 'bs-wh__main');
  const head = eyebrow(model.mode === 'zero' ? LABEL_POPULAR_PRODUCTS : LABEL_PRODUCTS, 'bs-wh-eyebrow--main');
  if (model.mode !== 'zero' && model.total > 0) {
    head.appendChild(el('span', 'bs-wh-count', resultsCount(model.total)));
  }
  main.appendChild(head);

  const list = el('ul', 'bs-wh-plist');
  products.forEach(function (card, i) {
    list.appendChild(buildProduct(card, i === 0 && model.top !== false));
  });
  main.appendChild(list);
  return main;
}

// ----------------------------------------------------------------- patka

function buildFoot(model) {
  const foot = el('div', 'bs-wh__foot');

  if (model.mode !== 'zero' && model.url && model.total > 0) {
    const cta = el('a', 'bs-wh__cta', resultsCta(model.total));
    cta.href = model.url;
    cta.insertAdjacentHTML('beforeend', iconHtml('arrow-right'));
    foot.appendChild(cta);
  } else {
    // bez dotazu není co „zobrazit všechno" → v patce zůstane jen nápověda
    foot.classList.add('bs-wh__foot--hint');
  }

  const hint = el('span', 'bs-wh__hint', LABEL_HINT);
  hint.setAttribute('aria-hidden', 'true');
  foot.appendChild(hint);
  return foot;
}

// ------------------------------------------------------------------ panel

/**
 * Obsah panelu z modelu. Vrací DocumentFragment k jednorázovému
 * `replaceChildren()` — panel se tím **vymění, ne přestaví**, takže mezi
 * dotazy nikdy neprobliká prázdný stav (akceptační bod 4 issue #97).
 *
 * @param {object} model
 * @param {'query'|'zero'} model.mode
 * @param {Array<{query: string, hit?: string, rest?: string}>} [model.suggests]
 * @param {Array<{kind: string, items: Array}>} [model.groups]
 * @param {Array<object>} [model.products] karty z API (`card` dle kontraktu)
 * @param {number} [model.total]
 * @param {string} [model.url] odkaz na stránku výsledků
 * @param {boolean} [model.top] `false` vypne chip TOP u prvního produktu
 * @param {(q: string) => string} model.urlFor odkaz pro našeptaný dotaz
 * @returns {DocumentFragment} prázdný, když model nemá co ukázat
 */
export function renderWhisperer(model) {
  const frag = document.createDocumentFragment();

  const side = el('div', 'bs-wh__side');
  const suggests = buildSuggests(
    model.suggests,
    model.mode === 'zero' ? LABEL_POPULAR_QUERIES : LABEL_SUGGESTS,
    model.urlFor,
  );
  if (suggests) side.appendChild(suggests);
  (model.groups || []).forEach(function (group) {
    const node = buildGroup(group);
    if (node) side.appendChild(node);
  });
  if (side.children.length) frag.appendChild(side);

  const main = buildProducts(model);
  if (main) frag.appendChild(main);

  // Patka dává smysl jen se sloupcem, ke kterému patří — prázdný model vrátí
  // prázdný fragment a volající degraduje na nativ.
  if (frag.childNodes.length) frag.appendChild(buildFoot(model));

  return frag;
}

/**
 * Skeleton na dobu prvního requestu (`bs-ac-pending`). Kreslí se JEN když
 * panel nemá co ukázat — při přepisu dotazu zůstává starý obsah (bod 4).
 */
export function renderSkeleton() {
  const frag = document.createDocumentFragment();

  const side = el('div', 'bs-wh__side');
  for (let i = 0; i < 5; i += 1) side.appendChild(el('span', 'bs-wh-skel bs-wh-skel--line'));
  frag.appendChild(side);

  const main = el('div', 'bs-wh__main');
  for (let i = 0; i < 4; i += 1) {
    const row = el('span', 'bs-wh-skel-row');
    row.appendChild(el('span', 'bs-wh-skel bs-wh-skel--thumb'));
    const lines = el('span', 'bs-wh-skel-row__lines');
    lines.appendChild(el('span', 'bs-wh-skel bs-wh-skel--line'));
    lines.appendChild(el('span', 'bs-wh-skel bs-wh-skel--line bs-wh-skel--short'));
    row.appendChild(lines);
    main.appendChild(row);
  }
  frag.appendChild(main);

  return frag;
}
